import type { Bankruptcy } from '@zg-rentals/ts-capps-client';
import { OLD_ADDRESS_CODE } from '../../constants/credit-report-constants';
import type {
  AddressInformation,
  EmploymentInformation,
  FullCreditProfile,
  PublicRecord,
} from '@zg-rentals/ts-capps-client';
import { getCurrencyDisplayFromDollars, getTitleCase } from '@zg-rentals/formatters';

export const getAddresses = (creditReport: {
  fullCreditProfile?: Pick<FullCreditProfile, 'AddressInformation'>;
}): Array<Array<string>> => {
  const { fullCreditProfile } = creditReport;

  if (fullCreditProfile?.AddressInformation) {
    const { AddressInformation: addresses } = fullCreditProfile;
    if (addresses && Array.isArray(addresses) && !isAllNullValues(addresses)) {
      const sortedAddresses = sortByDate(addresses, 'FirstReportedDate');
      return sortedAddresses.map((address) => getFormattedAddress(address as AddressInformation));
    }
  }
  return [];
};

export const getEmployers = (creditReport: {
  fullCreditProfile?: Pick<FullCreditProfile, 'EmploymentInformation'>;
}) => {
  const { fullCreditProfile } = creditReport;
  const employers: Array<{ employerName: string; address: string }> = [];

  if (
    fullCreditProfile &&
    fullCreditProfile.EmploymentInformation &&
    Array.isArray(fullCreditProfile.EmploymentInformation)
  ) {
    const { EmploymentInformation } = fullCreditProfile;

    EmploymentInformation.forEach((employee) => {
      const { Name } = employee;
      if (Name) {
        const employerName = getTitleCase(Name);
        const address = getEmployerAddress(employee);
        employers.push({
          employerName,
          address,
        });
      }
    });

    const sortedEmployers = sortByDate(employers, 'FirstReportedDate');

    return sortedEmployers;
  }
  return [];
};

type PublicRecordType = Pick<PublicRecord, 'Status' | 'Amount' | 'Court' | 'FilingDate' | 'StatusDate'> & {
  Bankruptcy: Pick<Bankruptcy, 'AssetAmount' | 'LiabilitiesAmount'>;
};

export const getBankruptcies = (creditReport: {
  fullCreditProfile?: {
    PublicRecord?: Array<PublicRecordType>;
  };
}) => {
  const { fullCreditProfile } = creditReport;

  if (fullCreditProfile?.PublicRecord && Array.isArray(fullCreditProfile.PublicRecord)) {
    const { PublicRecord } = fullCreditProfile;
    const bankruptcies: Array<{ filingDate: string; bankruptcyContent: Array<string> }> = [];

    PublicRecord.filter((record) => !!record).forEach((record) => {
      const { FilingDate } = record;

      const content = getBankruptcyInformation(record);
      if (content !== null && content.length > 0) {
        bankruptcies.push({
          filingDate: FilingDate,
          bankruptcyContent: content,
        });
      }
    });

    const sortedBankruptcies = sortByDate(bankruptcies, 'FilingDate');

    return sortedBankruptcies;
  }
  return [];
};

export const getBankruptcyInformation = (bankruptcy: PublicRecordType) => {
  const bankruptcyCodes = ['13', '15', '16', '17', '22', '23', '24', '25', '26', '27', '28', '29'];

  if (bankruptcy !== null && typeof bankruptcy === 'object' && !Array.isArray(bankruptcy)) {
    const {
        Status: statusDesc,
        Amount: amount,
        Bankruptcy: bankruptcyField,
        Court: court,
        FilingDate: filingDate,
        StatusDate: statusDate,
      } = bankruptcy,
      bankruptcyContent = [];

    if (statusDesc?.code && bankruptcyCodes.includes(statusDesc.code)) {
      if (statusDesc.desc) {
        bankruptcyContent.push(statusDesc.desc);
      }

      bankruptcyContent.push('Amount: ' + (getCurrencyDisplayFromDollars({ amount, hideCents: true }) || 'N/A'));

      if (bankruptcyField) {
        const { LiabilitiesAmount: liabilitiesAmount, AssetAmount: assetsAmount } = bankruptcyField,
          liabilitiesAmountString = getCurrencyDisplayFromDollars({ amount: liabilitiesAmount, hideCents: true }),
          assetsAmountString = getCurrencyDisplayFromDollars({ amount: assetsAmount, hideCents: true });

        bankruptcyContent.push('Liability: ' + (liabilitiesAmountString || 'N/A'));
        bankruptcyContent.push('Assets plaintiff: ' + (assetsAmountString || 'N/A'));
      }

      if (court) {
        bankruptcyContent.push('Court: ' + (court && court.desc ? court.desc : 'N/A'));
      }

      bankruptcyContent.push('Filing Date: ' + (filingDate ? filingDate : 'N/A'));
      bankruptcyContent.push('Status Date: ' + (statusDate ? statusDate : 'N/A'));
    }

    return bankruptcyContent;
  }
  return ['Unavailable'];
};

const getFormattedAddress = (address: AddressInformation) => {
  const {
    StreetPrefix: streetPrefix,
    StreetName: streetName,
    StreetSuffix: streetSuffix,
    UnitType: unitType,
    UnitID: unitId,
    City: city,
    State: state,
    Zip: zip,
  } = address;
  const addressLines = [];

  let addressFirstLine = '';
  let addressSecondLine = '';

  if (streetPrefix) {
    addressFirstLine += streetPrefix;
  }
  if (streetName) {
    if (streetPrefix) {
      addressFirstLine += ' ';
    }
    addressFirstLine += streetName;
  }
  if (streetSuffix) {
    if (streetName) {
      addressFirstLine += ' ';
    }
    addressFirstLine += streetSuffix;
  }
  if (unitType) {
    if (streetName) {
      addressFirstLine += ' ';
    }
    addressFirstLine += unitType;
  }
  if (unitId) {
    if (streetName) {
      addressFirstLine += ' ';
    }
    addressFirstLine += unitId;
  }

  if (addressFirstLine) {
    addressLines.push(addressFirstLine);
  }

  if (city) {
    addressSecondLine += city;
  }
  if (state) {
    if (city) {
      addressSecondLine += ', ';
    }
    addressSecondLine += state;
  }
  if (zip) {
    if (state) {
      addressSecondLine += ' ';
    }
    addressSecondLine += zip;
  }

  if (addressSecondLine) {
    addressLines.push(addressSecondLine);
  }

  return addressLines;
};

function isEmptyOrOldDate(date?: string) {
  return !date || date === OLD_ADDRESS_CODE;
}

// Sorts for dates in MM/YYYY and MM/DD/YYYY formats
function sortByDate<T>(array: Array<T>, dateKey: string) {
  const newArray = [...array].sort(function (a, b) {
    // @ts-ignore
    let dateAString = a[dateKey] as string | undefined;
    // @ts-ignore
    let dateBString = b[dateKey] as string | undefined;
    if (isEmptyOrOldDate(dateAString)) {
      return isEmptyOrOldDate(dateBString) ? 0 : 1;
    }
    if (isEmptyOrOldDate(dateBString)) {
      return -1;
    }
    if (dateAString && dateAString.length < 8) {
      // date is in MM/YYYY format and we need to convert to MM/DD/YYYY
      const dateAParts = dateAString.split('/');
      dateAString = dateAParts[0] + '/01/' + dateAParts[1];
    }
    if (dateBString && dateBString.length < 8) {
      // date is in MM/YYYY format and we need to convert to MM/DD/YYYY
      const dateBParts = dateBString.split('/');
      dateBString = dateBParts[0] + '/01/' + dateBParts[1];
    }
    const dateA = new Date(dateAString || '');
    const dateB = new Date(dateBString || '');
    return dateB.getTime() - dateA.getTime();
  });
  return newArray;
}

const isAllNullValues = (array: Array<AddressInformation>) => {
  return array.every(
    (item) =>
      Object.keys(item).length === 0 ||
      Object.keys(item).every((key) => item[key as keyof AddressInformation] === null),
  );
};

const getEmployerAddress = (employer: EmploymentInformation) => {
  let address = '';
  const {
    AddressFirstLine: addressFirstLine,
    AddressSecondLine: addressSecondLine,
    AddressExtraLine: addressExtraLine,
    Zip: zip,
  } = employer;

  if (addressFirstLine) {
    address += addressFirstLine;
  }

  if (addressSecondLine) {
    if (addressFirstLine) {
      address += ', ';
    }
    // The zip code could be in the AddressSecondLine. If it is, we
    // will remove it here and add it at the end of the address string below
    const deconstructedAddressSecondLine = addressSecondLine.split(' ');
    if (deconstructedAddressSecondLine[deconstructedAddressSecondLine.length - 1] === zip) {
      address += addressSecondLine.substring(0, addressSecondLine.lastIndexOf(' '));
    } else {
      address += addressSecondLine;
    }
  }

  // The zip code could be in the AddressExtraLine. If it is, we
  // won't add it to the end of the address string below
  let showZip = true;
  if (addressExtraLine) {
    if (addressFirstLine || addressSecondLine) {
      address += ', ';
    }
    address += addressExtraLine;
    const deconstructedAddressExtraLine = addressExtraLine.split(' ');
    if (deconstructedAddressExtraLine[deconstructedAddressExtraLine.length - 1] === zip) {
      showZip = false;
    }
  }
  if (zip && showZip) {
    if (addressFirstLine || addressSecondLine || addressExtraLine) {
      address += ' ';
    }
    address += zip;
  }
  return address;
};
